import { Button, Divider, Switch } from '@nextui-org/react';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@nextui-org/react';
import { useNSFWSettings } from '@/lib/hooks/use-nsfw-settings';
import { Popover, PopoverTrigger, PopoverContent } from '@nextui-org/react';
import { Modal, ModalContent, ModalBody } from '@nextui-org/react';
import { useBreakpoint } from '@/lib/hooks/use-breakpoint';

interface NSFWContentProps {
  onConfirm?: () => void;
  onCancel?: () => void;
  className?: string;
  localMature: boolean;
  setLocalMature: (value: boolean) => void;
  localExplicit: boolean;
  setLocalExplicit: (value: boolean) => void;
  isDisabling?: boolean;
}

// Extracted reusable content component
export function NSFWContent({
  onConfirm,
  onCancel,
  className,
  localMature,
  setLocalMature,
  localExplicit,
  setLocalExplicit,
  isDisabling = false,
}: NSFWContentProps) {
  const { settings, updateSettings } = useNSFWSettings();
  const [isCancelling, setIsCancelling] = useState(false);

  const handleConfirm = () => {
    updateSettings({
      mature: localMature,
      explicit: localExplicit,
      blur: false,
      confirmed18: !localMature && !localExplicit ? false : true,
    });
    onConfirm?.();
  };

  const handleCancel = () => {
    setLocalMature(false);
    setLocalExplicit(false);
    setIsCancelling(true);
    setTimeout(() => {
      updateSettings({
        mature: false,
        explicit: false,
        blur: true,
        confirmed18: false,
      });
      onCancel?.();
      setIsCancelling(false);
    }, 700);
  };

  return (
    <div className={cn('nsfw-content space-y-2 sm:space-y-3', className)}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-small font-medium">Mature Content</div>
          <div className="text-tiny text-default-500">
            Show suggestive content and mild nudity site-wide
          </div>
        </div>
        <Switch
          classNames={{
            base: cn(
              'inline-flex w-10 bg-content1 hover:bg-content2',
              'justify-between cursor-pointer rounded-lg',
              'border-2 border-transparent',
              'group-data-[selected=true]:border-violet-500'
            ),
            wrapper: cn(
              'p-0 h-4 overflow-visible',
              '!bg-default-200',
              'group-data-[selected=true]:!bg-gradient-to-br',
              'group-data-[selected=true]:!from-violet-500',
              'group-data-[selected=true]:!to-fuchsia-500'
            ),
            thumb: cn(
              'w-6 h-6 border-2 shadow-lg',
              'group-data-[hover=true]:border-violet-500',
              'group-data-[selected=true]:ml-6',
              'group-data-[selected=true]:border-violet-500',
              'group-data-[pressed=true]:w-7',
              'group-data-[selected]:group-data-[pressed]:ml-4'
            ),
          }}
          size="sm"
          isSelected={localMature}
          onValueChange={setLocalMature}
        />
      </div>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-small font-medium">Explicit Content</div>
          <div className="text-tiny text-default-500">
            Show pornographic content site-wide
          </div>
        </div>
        <Switch
          classNames={{
            base: cn(
              'inline-flex w-10 bg-content1 hover:bg-content2',
              'justify-between cursor-pointer rounded-lg',
              'border-2 border-transparent',
              'group-data-[selected=true]:border-violet-500'
            ),
            wrapper: cn(
              'p-0 h-4 overflow-visible',
              '!bg-default-200',
              'group-data-[selected=true]:!bg-violet-500',
              'group-data-[selected=true]:!bg-gradient-to-br',
              'group-data-[selected=true]:!from-violet-500',
              'group-data-[selected=true]:!to-fuchsia-500'
            ),
            thumb: cn(
              'w-6 h-6 border-2 shadow-lg',
              'group-data-[hover=true]:border-violet-500',
              'group-data-[selected=true]:ml-6',
              'group-data-[selected=true]:border-violet-500',
              'group-data-[pressed=true]:w-7',
              'group-data-[selected]:group-data-[pressed]:ml-4'
            ),
          }}
          size="sm"
          isSelected={localExplicit}
          onValueChange={setLocalExplicit}
        />
      </div>
      <Divider className="my-2" />
      <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
        <Button
          color="danger"
          variant="light"
          size="sm"
          className="w-full sm:w-auto sm:min-w-[120px]"
          onClick={handleCancel}
          isLoading={isCancelling || isDisabling}
          spinner={
            <svg
              className="h-4 w-4 animate-spin text-current"
              fill="none"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          }
        >
          {isCancelling || isDisabling ? 'Saving...' : 'Disable All'}
        </Button>
        <Button
          className={cn(
            'w-full sm:w-auto sm:min-w-[217px]',
            'border-0 bg-gradient-to-br from-violet-500 to-fuchsia-500 font-medium text-white hover:from-violet-600 hover:to-fuchsia-600',
            !localMature && !localExplicit
              ? '!hover:from-brand-dark !hover:to-brand-dark !bg-brand !from-brand !to-brand'
              : ''
          )}
          onClick={handleConfirm}
        >
          {!localMature && !localExplicit ? (
            'Confirm Settings'
          ) : (
            <ButtonText
              text1="18+"
              text2={window.innerWidth < 640 ? 'Confirm' : 'Confirm Settings'}
            />
          )}
        </Button>
      </div>
    </div>
  );
}

// Simple toggle button component for non-modal uses
interface NSFWToggleProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onConfirm?: () => void;
}

// Update the animation keyframes
const pulsingBorderAnimation = `
  @keyframes pulseBorder {
    0% {
      box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.4);
    }
    70% {
      box-shadow: 0 0 0 6px rgba(139, 92, 246, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(139, 92, 246, 0);
    }
  }
`;

// First, let's add a reusable button text component
const ButtonText = ({ text1, text2 }: { text1: string; text2: string }) => (
  <div className="flex items-center gap-2">
    <span>{text1}</span>
    <span className="h-4 w-[1px] bg-gradient-to-b from-white/10 via-white/50 to-white/10" />
    <span>{text2}</span>
  </div>
);

// Create a reusable trigger button component
function ToggleButton({
  settings,
  size,
  className,
  onClick,
}: {
  settings: any;
  size: 'sm' | 'md' | 'lg';
  className?: string;
  onClick?: () => void;
}) {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-9 h-9',
    lg: 'w-16 h-16',
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-4 h-4',
    lg: 'w-8 h-8',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        sizeClasses[size],
        'rounded-full',
        'flex items-center justify-center',
        'duration-200 transition-all',
        'outline-none focus:outline-none',
        settings.confirmed18
          ? [
              'bg-gradient-to-br from-violet-500 to-fuchsia-500',
              'hover:from-violet-600 hover:to-fuchsia-600',
              'border border-transparent',
              'animate-[pulseBorder_2s_infinite]',
              'relative',
              'after:absolute after:inset-[-2px] after:rounded-full after:bg-gradient-to-br after:from-violet-500/30 after:to-fuchsia-500/30 after:opacity-75 after:blur-sm',
            ]
          : [
              'relative',
              'bg-default-100 dark:bg-default-50',
              'border-1 border-pink-500/50 dark:border-purple-500/50',
              'animate-[pulseBorder_2s_infinite]',
              'hover:border-violet-500/50',
              'duration-200 transition-colors',
            ],
        className
      )}
    >
      <div className="flex h-full w-full items-center justify-center">
        {settings.confirmed18 ? (
          <EyeIcon className={cn(iconSizes[size], 'text-white')} />
        ) : (
          <EyeOffIcon className={cn(iconSizes[size], 'text-default-500')} />
        )}
      </div>
    </button>
  );
}

// Create a reusable content wrapper component
function ContentWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-1 py-2">
      <div className="text-small font-bold">Content Settings</div>
      {children}
    </div>
  );
}

export default function NSFWToggle({
  size = 'md',
  className,
}: NSFWToggleProps) {
  const isMobile = useBreakpoint() === 'xs';
  const { settings } = useNSFWSettings();
  const [isOpen, setIsOpen] = useState(false);
  const [localMature, setLocalMature] = useState(settings.mature);
  const [localExplicit, setLocalExplicit] = useState(settings.explicit);
  const [isDisabling, setIsDisabling] = useState(false);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setLocalMature(settings.mature || true);
      setLocalExplicit(settings.explicit || true);
    }
    setIsOpen(open);
  };

  const handleOutsideClick = () => {
    if (isOpen) {
      if (settings.mature && settings.explicit) {
        setIsOpen(false);
      } else {
        setIsDisabling(true);
        setLocalMature(settings.mature);
        setLocalExplicit(settings.explicit);
        setTimeout(() => {
          setIsOpen(false);
          setIsDisabling(false);
        }, 700);
      }
    }
    return false;
  };

  const contentProps = {
    localMature,
    setLocalMature,
    localExplicit,
    setLocalExplicit,
    onConfirm: () => setIsOpen(false),
    onCancel: () => setIsOpen(false),
    isDisabling,
    className: 'mt-4',
  };

  return (
    <>
      <style jsx global>
        {pulsingBorderAnimation}
      </style>

      {isMobile ? (
        <>
          <ToggleButton
            settings={settings}
            size={size}
            className={className}
            onClick={() => handleOpenChange(true)}
          />

          <Modal
            isOpen={isOpen}
            onClose={() => handleOutsideClick()}
            hideCloseButton
            className="m-0"
            classNames={{
              base: 'm-0',
              wrapper: 'items-end',
              body: 'p-0',
            }}
            motionProps={{
              variants: {
                enter: {
                  y: 0,
                  opacity: 1,
                  transition: { duration: 0.3, ease: 'easeOut' },
                },
                exit: {
                  y: 20,
                  opacity: 0,
                  transition: { duration: 0.2, ease: 'easeIn' },
                },
              },
            }}
          >
            <ModalContent>
              <ModalBody className="p-4">
                <ContentWrapper>
                  <NSFWContent {...contentProps} />
                </ContentWrapper>
              </ModalBody>
            </ModalContent>
          </Modal>
        </>
      ) : (
        <Popover
          isOpen={isOpen}
          onOpenChange={handleOpenChange}
          showArrow
          backdrop="opaque"
          placement="bottom"
          shouldCloseOnInteractOutside={handleOutsideClick}
          offset={10}
          classNames={{
            base: [
              'border-none',
              'bg-content1 dark:bg-content1',
              'p-3',
              'rounded-lg',
            ].join(' '),
            arrow: 'bg-content1',
            content: 'p-0 border-none rounded-lg',
            backdrop: 'pointer-events-none',
          }}
        >
          <PopoverTrigger>
            <div>
              <ToggleButton
                settings={settings}
                size={size}
                className={className}
                onClick={() => handleOpenChange(true)}
              />
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-96">
            <ContentWrapper>
              <NSFWContent {...contentProps} />
            </ContentWrapper>
          </PopoverContent>
        </Popover>
      )}
    </>
  );
}
